import React, { useState, useEffect } from 'react';
import { FiX, FiSave, FiLoader } from 'react-icons/fi';
import Swal from 'sweetalert2';
import { useUpdateNationality } from '../../hooks/useNationalities';
import Input from '../Input';

const EditNationality = ({ isOpen, onClose, nationality }) => {
  const [formData, setFormData] = useState({
    nationality: {
      ar: '',
      en: ''
    }
  });
  const [errors, setErrors] = useState({});

  const updateNationalityMutation = useUpdateNationality();

  // Initialize form data when nationality changes
  useEffect(() => {
    if (nationality) {
      setFormData({
        nationality: {
          ar: nationality.nationality?.ar || '',
          en: nationality.nationality?.en || ''
        }
      });
    }
  }, [nationality]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      nationality: {
        ...prev.nationality,
        [field]: value
      }
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nationality.en.trim()) {
      newErrors.en = 'English nationality is required';
    }

    if (!formData.nationality.ar.trim()) {
      newErrors.ar = 'Arabic nationality is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await updateNationalityMutation.mutateAsync({
        id: nationality.id,
        nationalityData: formData
      });
      
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Nationality updated successfully.',
        timer: 2000,
        showConfirmButton: false,
        background: '#ffffff',
        color: '#333',
        customClass: {
          popup: 'rounded-lg shadow-xl',
        }
      });

      onClose();
    } catch (error) {
      console.error('Error updating nationality:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to update nationality. Please try again.',
        confirmButtonText: 'OK',
        background: '#ffffff',
        color: '#333',
        customClass: {
          popup: 'rounded-lg shadow-xl',
        }
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity">
          <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen"></span>

        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Edit Nationality</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FiX className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                label="Nationality"
                name="nationality_en"
                value={formData.nationality.en}
                onChange={(e) => handleInputChange('en', e.target.value)}
                error={errors.en}
                placeholder="Enter nationality"
                required
              />
            </div>

            {/* <div>
              <Input
                label="Nationality (Arabic)"
                name="nationality_ar"
                value={formData.nationality.ar}
                onChange={(e) => handleInputChange('ar', e.target.value)}
                error={errors.ar}
                placeholder="أدخل الجنسية باللغة العربية"
                required
              />
            </div> */}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                disabled={updateNationalityMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updateNationalityMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {updateNationalityMutation.isPending ? (
                  <>
                    <FiLoader className="animate-spin h-4 w-4 mr-2" />
                    Updating...
                  </>
                ) : (
                  <>
                    <FiSave className="h-4 w-4 mr-2" />
                    Update Nationality
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditNationality; 